<template>
  <div v-if="imageUrl" class="editor">
    <div class="editor-top">
      <div class="sample-row">
        <label class="mini-field">
          <span>预览姓名</span>
          <input v-model.trim="localSampleName" type="text" placeholder="张三" @input="emitSample" />
        </label>
        <label class="mini-field">
          <span>预览职务</span>
          <input v-model.trim="localSampleIdentity" type="text" placeholder="秘书长" @input="emitSample" />
        </label>
      </div>
      <p class="editor-hint">点击文字可选中；在预览区拖动调整位置。修改样式即时生效，保存模板后生效于打印与小程序。</p>
    </div>

    <div class="editor-body">
      <div
        ref="stage"
        class="stage"
        :class="layoutClass"
        @mousedown.self="selected = ''"
      >
        <img class="stage-bg" :src="bgSrc" alt="" draggable="false" @mousedown.prevent />
        <div
          class="slot-text"
          :class="{ 'slot-text--active': selected === 'name' }"
          :style="nameCss"
          @mousedown.stop="startDrag('name', $event)"
        >
          {{ localSampleName || '姓名' }}
        </div>
        <div
          class="slot-text"
          :class="{ 'slot-text--active': selected === 'identity' }"
          :style="identityCss"
          @mousedown.stop="startDrag('identity', $event)"
        >
          {{ localSampleIdentity || '职务' }}
        </div>
      </div>

      <aside class="panel">
        <p class="panel-title">{{ selected === 'identity' ? '职务样式' : selected === 'name' ? '姓名样式' : '文字样式' }}</p>
        <p v-if="!selected" class="panel-muted">请先点击预览区中的「姓名」或「职务」</p>
        <template v-else>
          <div class="align-block">
            <span class="align-label">水平</span>
            <div class="align-btns">
              <button type="button" class="btn btn-edit btn-xs" @click="applyAlign('left', null)">左</button>
              <button type="button" class="btn btn-edit btn-xs" @click="applyAlign('center', null)">中</button>
              <button type="button" class="btn btn-edit btn-xs" @click="applyAlign('right', null)">右</button>
            </div>
          </div>
          <div class="align-block">
            <span class="align-label">垂直</span>
            <div class="align-btns">
              <button type="button" class="btn btn-edit btn-xs" @click="applyAlign(null, 'top')">上</button>
              <button type="button" class="btn btn-edit btn-xs" @click="applyAlign(null, 'middle')">中</button>
              <button type="button" class="btn btn-edit btn-xs" @click="applyAlign(null, 'bottom')">下</button>
            </div>
          </div>
          <label class="panel-field">
            <span>字号（px）</span>
            <input
              :value="activeSlot.fontSizePx"
              type="number"
              min="12"
              max="120"
              @input="setField('fontSizePx', Number($event.target.value))"
            />
          </label>
          <label class="panel-field">
            <span>颜色</span>
            <div class="color-row">
              <input
                :value="activeSlot.color"
                type="color"
                class="color-input"
                @input="setField('color', $event.target.value)"
              />
              <input
                :value="activeSlot.color"
                type="text"
                class="color-text"
                @input="setField('color', $event.target.value)"
              />
            </div>
          </label>
          <label class="panel-field">
            <span>粗细</span>
            <select
              :value="activeSlot.fontWeight"
              class="panel-select"
              @change="setField('fontWeight', $event.target.value)"
            >
              <option value="400">常规</option>
              <option value="500">中等</option>
              <option value="600">半粗</option>
              <option value="700">粗体</option>
            </select>
          </label>
          <label class="panel-field">
            <span>行距</span>
            <input
              :value="activeSlot.lineHeight"
              type="number"
              min="1"
              max="2.5"
              step="0.05"
              @input="setField('lineHeight', Number($event.target.value))"
            />
          </label>
          <p class="panel-pos">位置 {{ activeSlot.xPct.toFixed(1) }}% × {{ activeSlot.yPct.toFixed(1) }}%</p>
        </template>
      </aside>
    </div>
  </div>
  <p v-else class="editor-empty">上传底图后可在此拖动并调整文字样式</p>
</template>

<script>
import { BASE_URL } from '@/api'
import {
  DEFAULT_IDENTITY_SLOT,
  DEFAULT_NAME_SLOT,
  alignSlot,
  normalizeSlot,
  slotToCss,
} from '@/utils/badgeTextSlotStyle.js'

export default {
  name: 'BadgeTemplateEditor',
  props: {
    imageUrl: { type: String, default: '' },
    layoutType: { type: String, default: 'LANDSCAPE' },
    modelValue: { type: Object, required: true },
    sampleName: { type: String, default: '张三' },
    sampleIdentity: { type: String, default: '秘书长' },
  },
  emits: ['update:modelValue', 'update:sampleName', 'update:sampleIdentity'],
  data() {
    return {
      selected: 'name',
      localSampleName: this.sampleName,
      localSampleIdentity: this.sampleIdentity,
      drag: null,
    }
  },
  computed: {
    layoutClass() {
      const u = String(this.layoutType || '').toUpperCase()
      if (u === 'PORTRAIT') return 'stage--portrait'
      if (u === 'SQUARE') return 'stage--square'
      return 'stage--landscape'
    },
    bgSrc() {
      const u = String(this.imageUrl || '').trim()
      if (!u) return ''
      if (/^https?:\/\//i.test(u) || u.startsWith('data:')) return u
      return `${BASE_URL}${u.startsWith('/') ? '' : '/'}${u}`
    },
    draft() {
      return {
        name: normalizeSlot(this.modelValue?.name, DEFAULT_NAME_SLOT),
        identity: normalizeSlot(this.modelValue?.identity, DEFAULT_IDENTITY_SLOT),
      }
    },
    nameCss() {
      return slotToCss(this.draft.name)
    },
    identityCss() {
      return slotToCss(this.draft.identity)
    },
    activeSlot() {
      return this.selected === 'identity' ? this.draft.identity : this.draft.name
    },
  },
  watch: {
    sampleName(v) {
      this.localSampleName = v
    },
    sampleIdentity(v) {
      this.localSampleIdentity = v
    },
  },
  mounted() {
    window.addEventListener('mousemove', this.onDragMove)
    window.addEventListener('mouseup', this.endDrag)
  },
  beforeUnmount() {
    window.removeEventListener('mousemove', this.onDragMove)
    window.removeEventListener('mouseup', this.endDrag)
  },
  methods: {
    emitSample() {
      this.$emit('update:sampleName', this.localSampleName)
      this.$emit('update:sampleIdentity', this.localSampleIdentity)
    },
    emitDraft(name, identity) {
      this.$emit('update:modelValue', {
        name: normalizeSlot(name, DEFAULT_NAME_SLOT),
        identity: normalizeSlot(identity, DEFAULT_IDENTITY_SLOT),
      })
    },
    setField(key, value) {
      const defaults = this.selected === 'identity' ? DEFAULT_IDENTITY_SLOT : DEFAULT_NAME_SLOT
      const slot = normalizeSlot({ ...this.activeSlot, [key]: value }, defaults)
      if (this.selected === 'identity') {
        this.emitDraft(this.draft.name, slot)
      } else {
        this.emitDraft(slot, this.draft.identity)
      }
    },
    applyAlign(h, v) {
      if (!this.selected) return
      const base = this.selected === 'identity' ? this.draft.identity : this.draft.name
      const next = alignSlot(base, h, v)
      if (this.selected === 'identity') {
        this.emitDraft(this.draft.name, next)
      } else {
        this.emitDraft(next, this.draft.identity)
      }
    },
    startDrag(slot, ev) {
      // 避免选中文字时浏览器默认行为带动外层滚动
      if (ev && typeof ev.preventDefault === 'function') ev.preventDefault()
      this.selected = slot
      const el = this.$refs.stage
      if (!el) return
      const rect = el.getBoundingClientRect()
      const cur = slot === 'identity' ? this.draft.identity : this.draft.name
      this.drag = {
        slot,
        rect,
        offsetX: ev.clientX,
        offsetY: ev.clientY,
        startXPct: cur.xPct,
        startYPct: cur.yPct,
      }
    },
    onDragMove(ev) {
      if (!this.drag) return
      const { rect, offsetX, offsetY, startXPct, startYPct, slot } = this.drag
      const dx = ev.clientX - offsetX
      const dy = ev.clientY - offsetY
      const xPct = clamp(startXPct + (dx / rect.width) * 100, 2, 98)
      const yPct = clamp(startYPct + (dy / rect.height) * 100, 2, 98)
      const base = slot === 'identity' ? this.draft.identity : this.draft.name
      const next = { ...base, xPct, yPct }
      if (slot === 'identity') {
        this.emitDraft(this.draft.name, next)
      } else {
        this.emitDraft(next, this.draft.identity)
      }
    },
    endDrag() {
      this.drag = null
    },
  },
}

function clamp(n, min, max) {
  if (!Number.isFinite(n)) return min
  return Math.min(max, Math.max(min, n))
}
</script>

<style scoped>
.editor {
  margin-top: 12px;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}
.editor-empty {
  margin: 12px 0 0;
  font-size: 13px;
  color: #667085;
}
.editor-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #667085;
  line-height: 1.45;
}
.sample-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.mini-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #475467;
  flex: 1;
  min-width: 140px;
}
.mini-field span {
  font-weight: 600;
}
.mini-field input {
  height: 36px;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  box-sizing: border-box;
}
.editor-body {
  display: grid;
  grid-template-columns: 1fr 220px;
  gap: 12px;
  margin-top: 12px;
  align-items: start;
}
@media (max-width: 900px) {
  .editor-body {
    grid-template-columns: 1fr;
  }
}
.stage {
  position: relative;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.1);
  cursor: default;
  user-select: none;
  background: #e4e7ec;
}
.stage--landscape {
  aspect-ratio: 1.586 / 1;
}
.stage--portrait {
  aspect-ratio: 1 / 1.586;
  max-width: 360px;
}
.stage--square {
  aspect-ratio: 1 / 1;
  max-width: 400px;
}
.stage-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}
.slot-text {
  position: absolute;
  z-index: 2;
  max-width: 90%;
  white-space: pre-wrap;
  word-break: break-word;
  cursor: move;
  padding: 4px 8px;
  border-radius: 4px;
  border: 2px solid transparent;
  box-sizing: border-box;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.4);
}
.slot-text--active {
  border-color: #2f6af6;
  background: rgba(47, 106, 246, 0.08);
}
.panel {
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
}
.panel-title {
  margin: 0 0 10px;
  font-weight: 600;
  color: #101828;
}
.panel-muted {
  margin: 0;
  color: #98a2b3;
  font-size: 12px;
  line-height: 1.4;
}
.align-block {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.align-label {
  width: 36px;
  flex-shrink: 0;
  font-size: 12px;
  color: #667085;
}
.align-btns {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.btn-xs {
  padding: 4px 10px;
  font-size: 12px;
}
.panel-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
  color: #344054;
}
.panel-field > span {
  font-weight: 600;
  font-size: 12px;
}
.panel-field input,
.panel-select {
  height: 36px;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  box-sizing: border-box;
}
.color-row {
  display: flex;
  gap: 8px;
  align-items: center;
}
.color-input {
  width: 44px;
  height: 36px;
  padding: 2px;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  cursor: pointer;
}
.color-text {
  flex: 1;
  min-width: 0;
}
.panel-pos {
  margin: 4px 0 0;
  font-size: 11px;
  color: #98a2b3;
}
.btn {
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
}
.btn-edit {
  border-color: #bfc9db;
  background: #fff;
  color: #26457d;
}
</style>
